import React, { useRef, useState } from 'react';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription,
  DrawerTrigger, DrawerFooter, DrawerClose,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api, errText } from '@/lib/api';
import { useLang, LangToggle } from '@/lib/i18n';
import { useKeyboardOpen, keyboardFlowHandlers } from '@/lib/useKeyboardOpen';
import { toast } from 'sonner';
import { LifeBuoy, Phone, PauseCircle, HelpCircle, Sparkles, Loader2, Send, MessageSquareText, ChevronDown } from 'lucide-react';

// AI quick-answers for patients (grounded in the patient guide, no PHI needed).
function PatientAskBox({ token }) {
  const { t } = useLang();
  const [q, setQ] = useState('');
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    const question = q.trim();
    if (question.length < 4) return;
    setBusy(true);
    setAnswer('');
    try {
      // The token lets the server scrub this patient's name and employer before
      // the model sees the question, and meter the call to their practice.
      const r = await api.post('/help/ask', { question, role: 'patient', token: token || '' });
      setAnswer(r.data.answer);
    } catch (e) {
      toast.error(errText(e, t('help.askUnavailable')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border bg-card p-4 space-y-2.5" data-testid="patient-help-ask">
      <div className="flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <div className="text-sm text-muted-foreground leading-6">
          <span className="font-semibold text-foreground">{t('help.askTitle')}</span> {t('help.askHint')}
        </div>
      </div>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !busy && ask()} placeholder={t('help.askPlaceholder')} className="h-11 rounded-xl text-[15px]" data-testid="patient-help-ask-input" />
        <Button onClick={ask} disabled={busy || q.trim().length < 4} size="icon" className="h-11 w-11 rounded-xl shrink-0" data-testid="patient-help-ask-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      {answer && <div className="rounded-lg bg-secondary/60 border px-3 py-2.5 text-[15px] leading-6 whitespace-pre-wrap" data-testid="patient-help-ask-answer">{answer}</div>}
    </div>
  );
}

// Escalation: message lands in the platform support desk (with the practice attached).
function PatientSupportBox({ token }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/help/tickets/patient', { token, subject, message });
      setSent(true);
    } catch (e) {
      toast.error(errText(e, t('help.ticketError')));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-xl border bg-card p-4 text-sm text-muted-foreground leading-6" data-testid="patient-help-ticket-sent">
        <span className="font-semibold text-foreground">{t('help.ticketSentTitle')}</span> {t('help.ticketSentBody')}
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card p-4 space-y-2.5" data-testid="patient-help-ticket">
      <button type="button" className="flex items-start gap-3 text-left w-full" onClick={() => setOpen(!open)} data-testid="patient-help-ticket-toggle">
        <MessageSquareText className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <div className="text-sm text-muted-foreground leading-6">
          <span className="font-semibold text-foreground">{t('help.stuckTitle')}</span> {t('help.stuckHint')}
        </div>
      </button>
      {open && (
        <div className="space-y-2">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t('help.ticketSubject')} className="h-11 rounded-xl text-[15px]" data-testid="patient-help-ticket-subject" />
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder={t('help.ticketMessage')} className="rounded-xl text-[15px]" data-testid="patient-help-ticket-message" />
          <Button onClick={submit} disabled={busy || !subject.trim() || !message.trim()} className="w-full h-11 rounded-xl" data-testid="patient-help-ticket-submit">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t('help.ticketSend')}
          </Button>
        </div>
      )}
    </div>
  );
}

// "Need help?" drawer shared by all patient screens.
export function HelpDrawer({ practice, token, triggerTestId = 'patient-need-help-button', triggerClassName = '' }) {
  const { t } = useLang();
  return (
    <Drawer>
      <DrawerTrigger asChild>
        <Button variant="ghost" size="sm" aria-label={t('layout.needHelp')} className={`h-10 rounded-xl text-[15px] shrink-0 px-2.5 sm:px-3 ${triggerClassName}`} data-testid={triggerTestId}>
          <LifeBuoy className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t('layout.needHelp')}</span>
        </Button>
      </DrawerTrigger>
      <DrawerContent data-theme="patient" className="patient-wash">
        <div className="mx-auto w-full max-w-[560px] px-4 sm:px-6">
          <DrawerHeader className="px-0">
            <DrawerTitle className="text-xl">{t('help.title')}</DrawerTitle>
            <DrawerDescription className="text-[15px] leading-6">
              {t('help.subtitle')}
            </DrawerDescription>
          </DrawerHeader>
          <div className="space-y-3 pb-2 max-h-[55vh] overflow-y-auto">
            <PatientAskBox token={token} />
            {/* Everything else is one tap away, so the drawer opens on the one
                thing most people want (full-flow review 2026-09-06 §2). */}
            <Collapsible>
              <CollapsibleTrigger asChild>
                <button type="button" className="w-full min-h-[48px] flex items-center justify-between rounded-xl border bg-card px-4 text-[15px] font-medium" data-testid="patient-help-more">
                  {t('help.moreWays')} <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 pt-3">
                {token && <PatientSupportBox token={token} />}
                <div className="rounded-xl border bg-card p-4 flex items-start gap-3">
                  <Phone className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-[15px]" data-testid="help-practice-name">{practice?.name || t('welcome.yourCareTeam')}</div>
                    <div className="text-sm text-muted-foreground leading-6">
                      {practice?.address || ''}
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border bg-card p-4 flex items-start gap-3">
                  <PauseCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="text-sm text-muted-foreground leading-6">
                    <span className="font-semibold text-foreground">{t('welcome.pauseTitle')}.</span> {t('welcome.pauseText')}
                  </div>
                </div>
                <div className="rounded-xl border bg-card p-4 flex items-start gap-3">
                  <HelpCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="text-sm text-muted-foreground leading-6">
                    <span className="font-semibold text-foreground">{t('form.almostThere')}</span> {t('form.officeWillFill')}: “{t('form.askOffice')}”.
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
          <DrawerFooter className="px-0">
            <DrawerClose asChild>
              <Button className="h-14 rounded-xl text-base font-semibold" data-testid="help-drawer-close-button">{t('docs.gotIt')}</Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

// Warm, mobile-first shell for all public patient screens.
// Applies the scoped patient theme so staff portals are unaffected.
// The sticky footer + safe-area padding fixes the P0 cropped-Continue bug.
export function PatientLayout({ practice, right, children, footer, testId = 'patient-layout' }) {
  const { t } = useLang();
  // While the phone keyboard is up, the header scrolls away and the Continue bar
  // steps aside, so the field being typed in gets the screen; return moves to
  // the next field and, on the last one, closes the keyboard to bring it back.
  const typing = useKeyboardOpen();
  const mainRef = useRef(null);
  const flow = keyboardFlowHandlers(mainRef);
  const initial = (practice?.name || 'C')[0].toUpperCase();
  const logo = practice?.logo || '';
  return (
    <div data-theme="patient" className="relative min-h-dvh flex flex-col patient-wash patient-paper" data-testid={testId}>
      <header className={`${typing ? 'relative' : 'sticky top-0'} z-30 border-b border-border/70 bg-background/85 backdrop-blur`}>
        <div className="mx-auto w-full max-w-[560px] px-4 sm:px-6 h-14 flex items-center gap-2.5">
          {logo ? (
            <img src={logo} alt={t('layout.logoAlt', { name: practice?.name || '' })} className="h-9 w-auto max-w-[80px] sm:max-w-[120px] object-contain shrink-0" data-testid="patient-header-practice-logo" />
          ) : (
            <div className="h-8 w-8 rounded-xl bg-primary text-primary-foreground grid place-items-center font-bold text-sm shrink-0">{initial}</div>
          )}
          <div className="flex-1 min-w-0 font-semibold tracking-tight text-[15px] truncate" data-testid="patient-header-practice-name">{practice?.name || ''}</div>
          <LangToggle />
          {right}
        </div>
      </header>
      <main ref={mainRef} onFocus={flow.onFocus} onKeyDown={flow.onKeyDown} className="flex-1 mx-auto w-full max-w-[560px] px-4 sm:px-6 pt-6 pb-16 text-[17px] leading-7">
        {children}
      </main>
      {footer && !typing && (
        <div className="sticky bottom-0 z-20 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 patient-sticky-cta">
          <div className="mx-auto w-full max-w-[560px] px-4 sm:px-6 pt-3">{footer}</div>
        </div>
      )}
    </div>
  );
}

export function PatientLoading() {
  const { t } = useLang();
  return (
    <div data-theme="patient" className="min-h-dvh patient-wash grid place-items-center text-muted-foreground text-[17px]">
      {t('layout.loading')}
    </div>
  );
}

export function PatientError({ message }) {
  const { t } = useLang();
  return (
    <div data-theme="patient" className="min-h-dvh patient-wash grid place-items-center p-6">
      <Card className="max-w-md rounded-2xl">
        <CardContent className="p-6 space-y-2">
          <div className="font-semibold text-lg">{t('layout.linkProblem')}</div>
          <p className="text-[15px] text-muted-foreground leading-6" data-testid="patient-link-error">{message}</p>
          <p className="text-[15px] text-muted-foreground leading-6">{t('layout.linkProblemHint')}</p>
        </CardContent>
      </Card>
    </div>
  );
}
